import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import type { ExceptionalDate, Interval } from '@gql-generated/graphql'
import moment from 'moment'
import { Fragment } from 'react'
import { formatOpeningHourIntervalPart } from '../../services/pointOfSale.formatting'
import { mapDayOpeningHoursToOpeningStatus } from '../../services/pointOfSale.mapping'
import type { DayOpeningHours } from '../../types.tmp'
import { openingStatusCssClasses } from '../point-of-sale-opening-status/PointOfSaleOpeningStatus.client'

const dayOfWeek = moment().isoWeekday() // 1 = Monday, 7 = Sunday

// #region OpeningHours

type OpeningHoursProps = {
  intervals: Interval[]
}

export function OpeningHoursIntervals({ intervals }: OpeningHoursProps): React.ReactElement {
  const { t } = useTranslation(['pointOfSale'])

  return (
    <span className="inline-flex flex-wrap">
      {intervals.length > 0 ? (
        intervals.map((interval, index) => {
          const begin = formatOpeningHourIntervalPart({ dayOfWeek, stringHour: interval.begin })
          const end = formatOpeningHourIntervalPart({ dayOfWeek, stringHour: interval.end })

          return (
            <Fragment key={`${interval.begin}-${interval.end}`}>
              <span className="whitespace-nowrap">
                {begin} - {end}
              </span>
              {index < intervals.length - 1 && <span>,&nbsp;</span>}
            </Fragment>
          )
        })
      ) : (
        <span>{t('pointOfSale:openingHours.closed')}</span>
      )}
    </span>
  )
}

// #endregion

// #region DayLine

type DayLineProps = {
  dayOpeningHours: DayOpeningHours
  exceptionalDates: ExceptionalDate[]
}

function DayLine({ dayOpeningHours, exceptionalDates }: DayLineProps): React.ReactElement {
  const isCurrentDay = dayOpeningHours.dayOfWeek === moment().isoWeekday()
  const openingStatus = mapDayOpeningHoursToOpeningStatus(dayOpeningHours, exceptionalDates)
  const { t } = useTranslation(['pointOfSale'])

  return (
    <>
      <dt
        className={twMerge(
          'min-w-11 capitalize',
          isCurrentDay && 'font-bold',
          isCurrentDay && openingStatus && openingStatusCssClasses[openingStatus.status],
        )}
      >
        {moment(dayOpeningHours.dayOfWeek, 'd').format('dddd')}
      </dt>
      <dd
        className={twMerge(
          isCurrentDay && 'font-bold',
          isCurrentDay && openingStatus && openingStatusCssClasses[openingStatus.status],
        )}
      >
        {openingStatus?.status !== 'exceptionally_closed' ? (
          <>
            {dayOpeningHours.intervals && (
              <OpeningHoursIntervals intervals={dayOpeningHours.intervals} />
            )}
          </>
        ) : (
          <span>{t('pointOfSale:openingHours.closed')}</span>
        )}
      </dd>
    </>
  )
}

// #endregion

// #region DisplayPointOfSaleOpeningHoursList

type PointOfSaleOpeningHoursListProps = {
  dayOpeningHours: DayOpeningHours[]
  exceptionalDates: ExceptionalDate[]
  showTitle?: boolean

  /** Additional classes to add to the component */
  className?: string
}

export function PointOfSaleOpeningHoursList({
  dayOpeningHours,
  exceptionalDates,
  showTitle = true,
  className,
}: PointOfSaleOpeningHoursListProps): React.ReactElement {
  const { t } = useTranslation(['pointOfSale'])

  return (
    <div className={className}>
      {showTitle && <p className="mb-0.5 font-bold">{t('pointOfSale:openingHours.title')}</p>}
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5">
        {dayOpeningHours.map((dayOpeningHour) => (
          <DayLine
            key={dayOpeningHour.dayOfWeek}
            dayOpeningHours={dayOpeningHour}
            exceptionalDates={exceptionalDates}
          />
        ))}
      </dl>
    </div>
  )
}

// #endregion
